import React, { useMemo, useState } from "react";
import { Modal, ModalHeader } from "../../globalComponents/ui/Modal";
import { SettingsModalBody, SettingsModalFooter } from "../Settings/SettingShell";
import { SecondaryButton, DangerButton } from "../../globalComponents/ui/Button";
import EvidenceListView, { ListRow } from "../EvidenceLibrary/components/EvidenceListView";
import DocumentPreviewModal from "../DataManagment/components/DocumentPreviewModal";
import { deleteDocument } from "../../_services/documentsService";
import { usePermission } from "../../hooks/usePermission";

export const getDocumentTitle = (doc) => doc?.fileMetadata?.fileName || doc?.fileName || "Untitled document";

export const getUploadedBy = (doc) => {
  if (!doc?.createdBy) return "—";
  const { first_name, last_name, email } = doc.createdBy;
  if (first_name || last_name) return `${first_name ?? ""} ${last_name ?? ""}`.trim();
  return email ?? "—";
};

const fyLabel = (id, financialYears) => financialYears.find((fy) => String(fy.id) === String(id))?.financial_year_value || "—";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};

const COLUMNS = [
  { key: "name", label: "Name", flex: 2 },
  { key: "fy", label: "Financial Year", width: 140 },
  { key: "uploadedBy", label: "Uploaded By", width: 150 },
  { key: "created", label: "Uploaded", width: 120 },
  { key: "actions", label: "", width: 110 },
];

/**
 * List view for the Policy Library — one flat list of company-wide
 * policy documents, each tied to a financial year. Mirrors the Evidence
 * Library's list chrome/row style for a consistent look across every
 * document listing in the app.
 */
const DocumentList = ({ documents = [], financialYears, isLoading, error, onRetry, onRefresh }) => {
  const [canDelete] = usePermission("policy-library.delete-policy", "LIST_POLICY");
  const [canView] = usePermission("policy-library.view-policy", "LIST_POLICY");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const rows = useMemo(
    () => [...documents].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || "")),
    [documents]
  );

  const confirmDelete = async () => {
    if (!deleteDoc) return;
    setDeleting(true);
    try {
      await deleteDocument(deleteDoc.id);
      setDeleteDoc(null);
      onRefresh();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <EvidenceListView
        columns={COLUMNS}
        rows={rows}
        rowKey={(d) => d.id}
        isLoading={isLoading}
        error={error}
        onRetry={onRetry}
        emptyTitle="No documents uploaded yet"
        emptySub="Policy documents you upload appear here, grouped by financial year."
        footer={<span>Showing {rows.length} document{rows.length === 1 ? "" : "s"}</span>}
        renderRow={(doc, index) => (
          <ListRow key={doc.id} index={index}>
            <div style={{ flex: 2, padding: "0 10px", minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="fas fa-file-alt" style={{ color: "#2563eb", fontSize: 13 }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={getDocumentTitle(doc)}>
                {getDocumentTitle(doc)}
              </div>
            </div>
            <div style={{ width: 140, padding: "0 10px", fontSize: 12, color: "#475569" }}>
              {fyLabel(doc.financialYearId, financialYears)}
            </div>
            <div style={{ width: 150, padding: "0 10px", fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {getUploadedBy(doc)}
            </div>
            <div style={{ width: 120, padding: "0 10px", fontSize: 11.5, color: "#94a3b8" }}>
              {fmtDate(doc.createdAt)}
            </div>
            <div style={{ width: 110, padding: "0 10px", display: "flex", gap: 6 }}>
              {canView && (
                <button
                  onClick={() => setPreviewDoc(doc)}
                  title="Preview"
                  aria-label="Preview document"
                  style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#3f88a5", cursor: "pointer" }}
                >
                  <i className="fas fa-eye" style={{ fontSize: 12 }} />
                </button>
              )}
              {canView && doc.fileMetadata?.url && (
                <a
                  href={doc.fileMetadata.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download"
                  aria-label="Download document"
                  style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
                >
                  <i className="fas fa-download" style={{ fontSize: 12 }} />
                </a>
              )}
              {canDelete && (
                <button
                  onClick={() => setDeleteDoc(doc)}
                  title="Delete"
                  aria-label="Delete document"
                  style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid #fecaca", background: "#fff", color: "#dc2626", cursor: "pointer" }}
                >
                  <i className="fas fa-trash" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
          </ListRow>
        )}
      />

      <DocumentPreviewModal document={previewDoc} onClose={() => setPreviewDoc(null)} />

      <Modal show={!!deleteDoc} onClose={() => setDeleteDoc(null)} maxWidth={420}>
        <ModalHeader icon="fas fa-exclamation-triangle" title="Delete document" onClose={() => setDeleteDoc(null)} />
        <SettingsModalBody>
          <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>
            Delete <strong>{deleteDoc && getDocumentTitle(deleteDoc)}</strong>? This action cannot be undone.
          </p>
        </SettingsModalBody>
        <SettingsModalFooter>
          <SecondaryButton onClick={() => setDeleteDoc(null)} disabled={deleting}>Cancel</SecondaryButton>
          <DangerButton onClick={confirmDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </DangerButton>
        </SettingsModalFooter>
      </Modal>
    </>
  );
};

export default DocumentList;
